<template>
  <div>
    <p class="mt-4" ref="title">
      <b>{{ name }}</b>
    </p>

    <v-list two-line>
      <v-list-tile v-for="(item, index) in value" :key="index">
        <v-list-tile-content
          @click="showPane(item, index)"
          style="cursor: pointer"
        >
          <v-list-tile-title>{{
            getItemById(item.id).label
          }}</v-list-tile-title>
          <v-list-tile-sub-title
            >ICD10: {{ getItemById(item.id).icd10 }}</v-list-tile-sub-title
          >
          <v-list-tile-sub-title
            v-if="item.qualifiers && item.qualifiers.length > 0"
            >{{ `Detalhes: ${getItemQualifiers(item)}` }}</v-list-tile-sub-title
          >
        </v-list-tile-content>
        <v-list-tile-action>
          <v-btn icon ripple @click="removeItem(item)">
            <v-icon>delete</v-icon>
          </v-btn>
        </v-list-tile-action>
      </v-list-tile>
      <v-divider></v-divider>
      <v-list-tile
        v-if="!showEditorPane && !(singleSelect && value.length > 0)"
      >
        <v-list-tile-content>
          <v-list-tile-title @click="showPane()" style="cursor: pointer"
            >Adicionar</v-list-tile-title
          >
        </v-list-tile-content>
        <v-list-tile-action>
          <v-btn icon ripple @click.stop="showPane()">
            <v-icon color="primary">add_circle</v-icon>
          </v-btn>
        </v-list-tile-action>
      </v-list-tile>
    </v-list>
    <div v-if="showEditorPane">
      <v-tabs slider-color="primary" v-model="activeTab">
        <v-tab :key="0">{{ $t('common.Search') }}</v-tab>
        <v-tab :key="1">{{ $tc('common.Tree') }}</v-tab>
        <v-tab v-if="!hasOtherProcedure" :key="2">Outro</v-tab>
        <v-tab-item>
          <v-autocomplete
            v-model="itemParent"
            @input="selectItem(itemParent)"
            :return-object="true"
            dont-fill-mask-blanks
            :filter="baseStringSearch"
            hide-no-data
            hide-selected
            :items="diagnoses"
            :label="$t('common.Search')"
            item-text="label"
            item-value="id"
            dense
            clearable
            :menu-props="{
              closeOnClick: false,
              closeOnContentClick: false,
              openOnClick: false,
              maxHeight: $vuetify.breakpoint.smAndDown ? 170 : 300
            }"
          />
          <v-autocomplete
            v-model="itemChild"
            @input="selectItem(itemChild)"
            auto-select-first
            :filter="baseStringSearch"
            v-if="itemHasChildren"
            :return-object="true"
            dont-fill-mask-blanks
            hide-no-data
            hide-selected
            :items="itemChildren"
            label="Especificar"
            item-text="label"
            item-value="id"
            :menu-props="{
              closeOnClick: false,
              closeOnContentClick: false,
              openOnClick: false,
              maxHeight: $vuetify.breakpoint.smAndDown ? 170 : 300
            }"
            dense
          />
        </v-tab-item>
        <v-tab-item>
          <v-treeview
            v-if="activeTab === 1"
            :items="diagnosisTree"
            item-text="label"
            :open="treeOpen"
            dense
          >
            <template v-slot:prepend="{ item }">
              <v-icon
                v-if="!!item.region && selectedItem.id !== item.id"
                @click="selectItem(item)"
                >radio_button_unchecked</v-icon
              >
              <v-icon
                v-if="
                  !!item.region &&
                    selectedItem.id &&
                    selectedItem.id === item.id
                "
                @click="selectItem(null)"
                >radio_button_checked</v-icon
              >
            </template>
            <template v-slot:label="{ item }">
              <span @click="!!item.region ? selectItem(item) : null">{{
                item.label
              }}</span>
            </template>
          </v-treeview>
        </v-tab-item>
        <v-tab-item v-if="!hasOtherProcedure">
          <v-flex xs12>
            <v-checkbox
              v-model="otherSelected"
              :value="other"
              label="Outro"
            ></v-checkbox>
          </v-flex>
        </v-tab-item>
      </v-tabs>
      <v-layout wrap>
        <v-flex xs12 md8>
          <v-text-field
            color="primary"
            :label="singularName"
            disabled
            :value="selectedItem.label"
            class="mr-1"
          />
        </v-flex>
        <v-flex xs12 md4 ref="selectedItemView">
          <v-text-field
            color="primary"
            label="ICD10"
            disabled
            :value="selectedItem.icd10"
            class="mr-1"
          >
            <template v-slot:append v-if="selectedItem.icd10">
              <v-icon v-if="selectedItem.complete" color="primary"
                >checkbox</v-icon
              >
              <v-icon v-else color="black">help</v-icon>
              <!-- hint explaining why the icd code isn't complete -->
            </template>
          </v-text-field>
        </v-flex>
      </v-layout>
      <v-layout
        v-if="selectedItem.qualifiers && selectedItem.qualifiers.length > 0"
      >
        <b>Detalhes</b>
      </v-layout>
      <v-layout
        v-if="selectedItem.qualifiers && selectedItem.qualifiers.length > 0"
        wrap
      >
        <v-flex
          xs12
          sm6
          v-for="qualifier in sortedQualifiers"
          :key="qualifier.id"
        >
          <v-checkbox
            hide-details
            dense
            v-model="qualifiers"
            :value="qualifier"
            :label="qualifier.label"
          ></v-checkbox>
        </v-flex>
      </v-layout>
      <v-layout class="ma-2">
        <v-btn
          small
          :disabled="!selectedItem.id"
          @click.stop="editItem()"
          v-if="paneIndex !== null"
          >Editar</v-btn
        >
        <v-btn small :disabled="!selectedItem.id" @click.stop="addItem()" v-else
          >Adicionar</v-btn
        >
        <v-btn
          small
          color="#fb9db8"
          depressed
          @click.stop="showEditorPane = false"
          >Cancelar</v-btn
        >
      </v-layout>
    </div>
  </div>
</template>

<script>
import { GeneralSurgery, DiagnosisQualifiers } from '@/models/GeneralSurgery'
import baseStringSearch from '@/utils/baseStringSearch'

export default {
  props: {
    value: { type: Array, default: () => [] },
    orm: { type: Function },
    singularName: { type: String, default: '' },
    name: { type: String, default: '' },
    singleSelect: { type: Boolean, default: false },
    surgeryDiagnosisOther: { type: String },
    anatomicalRegions: { type: Array },
    region: { type: String },
  },
  data() {
    return {
      itemParent: '',
      itemChild: '',
      selectedItem: {},
      treeOpen: [],
      activeTab: '0',
      qualifiers: [],
      showEditorPane: false,
      paneIndex: null,
      otherSelected: false,
      other: {
        $id: "other",
        children: [],
        complete: null,
        icd10: "0Z",
        id: "other",
        label: "Outro",
        parent_id: null,
        qualifier_ids: [],
        qualifiers: [],
      }
    }
  },
  computed: {
    hasOtherProcedure() {
      return this.value.some(procedure => procedure.id === 'other')
    },
    sortedQualifiers() {
      return [...this.selectedItem.qualifiers].sort(
        (qualifierA, qualifierB) => {
          if (qualifierA.group && !qualifierB.group) return 1
          if (!qualifierA.group && qualifierB.group) return -1
          return qualifierA.label.toUpperCase() > qualifierA.label.toUpperCase()
            ? 1
            : -1
        }
      )
    },
    organTree() {
      return GeneralSurgery.query().first().organTree || []
    },
    diagnosisTree() {
      const diagnosisTree = []

      for (const region of this.organTree) {
        const newRegion = { ...region, children: [] }
        const regionDiagnoses = this.normalizedSort(
          this.getNodeDiagnoses(region)
        )
        newRegion.children.push(...regionDiagnoses)
        if (region.children) {
          for (const organ of region.children) {
            const newOrgan = { ...organ }

            const organDiagnoses = this.getNodeDiagnoses(organ)
            newOrgan.children = this.normalizedSort(organDiagnoses)
            if (organDiagnoses.length) {
              newRegion.children.push(newOrgan)
            }
          }
        }
        if (newRegion.children.length) {
          diagnosisTree.push(newRegion)
        }
      }

      return this.normalizedSort(diagnosisTree)
    },
    diagnoses() {
      return [...this.orm.query().get()]
    },
    diagnosisCategories() {
      return [
        ...this.orm
          .query()
          .where('parent_id', null)
          .with('children')
          .get()
      ]
    },
    itemHasChildren() {
      return this.itemChildren.length > 1
    },
    itemChildren() {
      return this.orm
        .query()
        .where('parent_id', (this.itemParent || {}).id)
        .get()
    }
  },
  watch: {
    otherSelected(val) {
      if(val) {
        this.selectedItem = val
      } else {
         this.selectedItem = {}
      }
    },
    qualifiers(newQualifiers, oldQualifiers) {
      if (newQualifiers.length > oldQualifiers.length) {
        const qualifier = newQualifiers.find(q => oldQualifiers.indexOf(q) < 0)
        if (qualifier.group) {
          const toRemove = newQualifiers.findIndex(
            q => q.group === qualifier.group && q.id !== qualifier.id
          )
          if (toRemove >= 0) newQualifiers.splice(toRemove, 1)
        }
      }
    }
  },
  methods: {
    baseStringSearch,
    getItemQualifiers(item) {
      if (!item.qualifiers) return ''
      return DiagnosisQualifiers.findIn(item.qualifiers)
        .map(qualifier => qualifier.label)
        .join(', ')
    },
    showPane(item = null, index = null) {
      if (item) {
        this.selectedItem = this.orm
          .query()
          .with('qualifiers')
          .find(item.id)
        this.qualifiers = this.selectedItem.qualifiers.filter(
          q => item.qualifiers.indexOf(q.id) >= 0
        )
        this.paneIndex = index
      } else {
        this.selectedItem = {}
        this.itemParent = ''
        this.itemChild = ''
        this.qualifiers = []
        this.paneIndex = null
      }
      this.showEditorPane = true
      if (this.$vuetify.breakpoint.xs) {
        this.$refs['title'].scrollIntoView({ behavior: 'smooth' })
      }
    },
    emitUpdate(newItems) {
      this.$emit('input', newItems)
    },
    addItem() {
      if (this.selectedItem.id) {
        this.emitUpdate([
          ...this.value,
          {
            id: this.selectedItem.id,
            qualifiers: this.qualifiers.map(qualifier => qualifier.id)
          }
        ])
        this.showEditorPane = false
      }
    },
    editItem() {
      let newItems = [...this.value]
      newItems[this.paneIndex] = {
        id: this.selectedItem.id,
        qualifiers: this.qualifiers.map(qualifier => qualifier.id)
      }
      // verificar
      this.emitUpdate(newItems)
      this.showEditorPane = false
    },
    removeItem(item) {
      if (this.paneIndex !== null) {
        this.paneIndex = null
        this.showEditorPane = false
      }
      this.emitUpdate(this.value.filter(element => element !== item))
    },
    normalizedSort(arr) {
      return [...arr].sort((a, b) => {
        const aLabel = (a.label || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
        const bLabel = (b.label || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
        if (aLabel < bLabel) return -1
        if (aLabel > bLabel) return 1
        return 0
      })
    },
    getNodeDiagnoses(node) {
      return this.diagnosisCategories.filter(
        diagnosis => diagnosis.region === node.id
      )
    },
    selectItem(target) {
      if (!target) {
        this.selectedItem = {}
        return
      }
      this.selectedItem = this.orm
        .query()
        .where('id', target.id)
        .with('qualifiers')
        .get()[0]
      this.qualifiers = []
      if (this.activeTab === 1 && this.$vuetify.breakpoint.xs) {
        this.$refs['selectedItemView'].scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      }
      if (
        this.selectedItem.qualifiers.length === 0 &&
        this.paneIndex === null
      ) {
        this.addItem()
      }
    },
    getItemById(targetId) {
      return this.orm.find(targetId)
    }
  }
}
</script>
